import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { cleanColisCode } from '../utils/helpers';

export default function ModalQrScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(true);

  useEffect(() => {
    let stream = null;
    let animationId = null;
    let detector = null;
    let isCancelled = false;

    const startCamera = async () => {
      try {
        if ('BarcodeDetector' in window) {
          try {
            detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          } catch {
            detector = null;
          }
        }

        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });

        if (isCancelled) {
          if (stream) stream.getTracks().forEach((track) => track.stop());
          return;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        const canvas = canvasRef.current || document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const detectFrame = async () => {
          if (isCancelled || !scanning || !videoRef.current) return;

          const video = videoRef.current;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            let foundCode = null;

            // 1. Native BarcodeDetector (Chrome avec flag)
            if (detector) {
              try {
                const barcodes = await detector.detect(video);
                if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                  foundCode = barcodes[0].rawValue;
                }
              } catch {
                // fallback vers jsQR
              }
            }

            // 2. Fallback universel jsQR (iOS Safari, Android Chrome, Firefox, Desktop)
            if (!foundCode && ctx) {
              try {
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 480;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height, {
                  inversionAttempts: 'dontInvert',
                });
                if (code && code.data) {
                  foundCode = code.data;
                }
              } catch {
                // frame skip
              }
            }

            if (foundCode) {
              extractAndTriggerCode(foundCode);
              return;
            }
          }

          if (!isCancelled && scanning) {
            animationId = requestAnimationFrame(detectFrame);
          }
        };

        animationId = requestAnimationFrame(detectFrame);
      } catch (err) {
        if (!isCancelled) {
          console.error('Camera access error:', err);
          setError("Impossible d'accéder à la caméra. Vérifiez les autorisations de votre navigateur ou utilisez un lien sécurisé (HTTPS).");
        }
      }
    };

    startCamera();

    return () => {
      isCancelled = true;
      setScanning(false);
      if (animationId) cancelAnimationFrame(animationId);
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, [scanning]);

  const extractAndTriggerCode = (value) => {
    if (!value) return;
    const cleaned = cleanColisCode(value);
    if (!cleaned) return;

    setScanning(false);
    onScan(cleaned);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      extractAndTriggerCode(manualCode.trim());
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.85)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 16,
    }}>
      <div style={{
        background: 'white',
        borderRadius: 24,
        width: '100%',
        maxWidth: 440,
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: '#0f172a',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ fontWeight: 900, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📷</span> Scanner le QR Code Colis
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* Caméra Viewport */}
        <div style={{ position: 'relative', background: '#000', height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {error ? (
            <div style={{ color: '#f87171', padding: 20, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>
              ⚠️ {error}
            </div>
          ) : (
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              playsInline
              muted
            />
          )}

          {/* Viseur Caméra */}
          <div style={{
            position: 'absolute',
            width: 200,
            height: 200,
            border: '3px solid #6366f1',
            borderRadius: 20,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: 2,
              background: '#ef4444',
              boxShadow: '0 0 8px #ef4444',
            }} />
          </div>
        </div>

        {/* Form Saisie Manuelle en bas */}
        <div style={{ padding: 20, background: '#f8fafc' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, textAlign: 'center' }}>
            Ou saisissez le code manuellement :
          </div>
          <form onSubmit={handleManualSubmit} style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Ex: ZKI-20260809-A1B2C3"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                fontSize: 14,
                fontWeight: 700,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                background: '#4F46E5',
                color: 'white',
                fontWeight: 900,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Valider
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
